/**
 * Client List Page Component
 *
 * Displays an enterprise DataGrid of advisor clients with virtualization,
 * filtering, sorting, and portfolio summary metrics.
 *
 * Read-only by design. There is no "Add New Client" CTA: App.tsx declares only
 * /clients and /clients/:clientId (no create route or page exists), the advisor
 * data backend is undeployed and stubbed (GH #1735), and the api-gateway that
 * would carry it is undeployed (GH #1670). advisor-tools.openapi.yaml declares
 * GET /api/clients, GET /api/clients/{clientId} and
 * GET /api/clients/{clientId}/positions only -- no create operation. Do not
 * re-add a create affordance until a POST contract, handler, route and page
 * exist; ClientListPage.test.tsx pins the absence.
 */
import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import type { ColumnDef } from '@aaimplatform/ui';
import {
  DataGrid,
  DashboardGrid,
  DashboardWidget,
  MetricCard,
  BodyText,
  Card,
} from '@aaimplatform/ui';


type ClientSummary = components['schemas']['ClientSummary'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientListPage() {
  const navigate = useNavigate();

  const { data: clients, isLoading, error } = useQuery<ClientSummary[]>({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error, response } = await advisorClient.GET('/api/clients', {
        params: { query: {} },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load clients');
      return data;
    },
  });

  // Portfolio summary metrics
  const metrics = useMemo(() => {
    if (!clients || clients.length === 0) {
      return { totalClients: 0, totalCollateral: 0, totalLoans: 0, avgCollateral: 0 };
    }
    const totalCollateral = clients.reduce((sum, c) => sum + c.totalCollateralValue, 0);
    const totalLoans = clients.reduce((sum, c) => sum + c.activeLoans, 0);
    return {
      totalClients: clients.length,
      totalCollateral,
      totalLoans,
      avgCollateral: totalCollateral / clients.length,
    };
  }, [clients]);

  // DataGrid column definitions (TanStack Table)
  const columns = useMemo<ColumnDef<ClientSummary, unknown>[]>(() => [
    {
      id: 'name',
      header: 'Client Name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${row.original.id}`);
          }}
          className="font-semibold text-accent hover:underline text-left"
        >
          {row.original.firstName} {row.original.lastName}
        </button>
      ),
      enableSorting: true,
      size: 200,
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      enableSorting: true,
      size: 220,
    },
    {
      id: 'phone',
      header: 'Phone',
      accessorKey: 'phone',
      enableSorting: false,
      size: 140,
      meta: { className: 'font-mono' },
    },
    {
      id: 'activeLoans',
      header: 'Active Loans',
      accessorKey: 'activeLoans',
      enableSorting: true,
      size: 120,
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="font-mono tabular-nums">{String(getValue())}</span>
      ),
    },
    {
      id: 'collateralValue',
      header: 'Collateral Value',
      accessorKey: 'totalCollateralValue',
      enableSorting: true,
      size: 160,
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="font-mono tabular-nums">{formatCurrency(Number(getValue()))}</span>
      ),
    },
    {
      id: 'lastActivity',
      header: 'Last Activity',
      accessorKey: 'lastActivity',
      enableSorting: true,
      size: 140,
      cell: ({ getValue }) => formatDate(String(getValue())),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      size: 160,
      cell: ({ row }) => (
        <div className="flex gap-3 items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/calculator?clientId=${row.original.id}`);
            }}
            className="text-accent text-sm hover:underline"
          >
            Calculate
          </button>
          <span className="text-tertiary">|</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/proposals/new?clientId=${row.original.id}`);
            }}
            className="text-accent text-sm hover:underline"
          >
            Propose
          </button>
        </div>
      ),
    },
  ], [navigate]);

  const handleRowClick = useCallback(
    (row: ClientSummary) => {
      navigate(`/clients/${row.id}`);
    },
    [navigate],
  );

  return (
    <div>
      {/* Portfolio Summary Metrics */}
      <DashboardGrid columns={4} gap="compact" className="mb-6">
        <DashboardWidget id="total-clients">
          <MetricCard
            label="Total Clients"
            value={String(metrics.totalClients)}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="total-collateral">
          <MetricCard
            label="Total Collateral"
            value={formatCurrency(metrics.totalCollateral)}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="active-loans">
          <MetricCard
            label="Active Loans"
            value={String(metrics.totalLoans)}
            valueColor="accent"
          />
        </DashboardWidget>
        <DashboardWidget id="avg-collateral">
          <MetricCard
            label="Avg Collateral / Client"
            value={formatCurrency(metrics.avgCollateral)}
            valueColor="primary"
          />
        </DashboardWidget>
      </DashboardGrid>

      {/* Client DataGrid */}
      {error instanceof AdvisorServiceMissingError ? (
        <AdvisorServiceNotice />
      ) : error ? (
        <Card>
          <div className="p-4 bg-error/10 border border-error/20 rounded">
            <BodyText className="text-error font-semibold">Error Loading Clients</BodyText>
            <BodyText className="text-error mt-1">
              Failed to retrieve client list. Verify the advisor-tools service is available.
            </BodyText>
          </div>
        </Card>
      ) : (
        <DataGrid
          data={clients ?? []}
          columns={columns}
          isLoading={isLoading}
          enableVirtualization
          virtualizedHeight={600}
          enableGlobalFilter
          enableColumnVisibility
          enableColumnFilters
          enablePagination
          defaultPageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
          enableExport
          enableRowSelection={false}
          striped
          hoverable
          density="default"
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          emptyMessage="No clients found. Adjust your search or filters."
        />
      )}
    </div>
  );
}
